import { useId, useMemo, useState } from 'react';
import _ from 'lodash';

import { Button } from '../shared/Button';
import { Input } from '../shared/Input';
import ModalV2, { Footer as ModalV2Footer } from '../shared/ModalV2';
import { PostData } from './model/PostTypes';
import { usePortalPostEditor } from './Post';
import { isTranslationTable } from './templates/inputs/TranslationTableInput';
import { LanguageType } from '@lifeserver/shared';

import { PortalLocalizationDTO } from '../hooks/portal';
import { useSelectedLanguage } from '../hooks/selectedLanugage';
import { useTranslations } from '../hooks/translations';

import { assert } from '../utils/assert';

import { isRecord } from '../types/isRecord';

import styles from './PortalTranslationManager.module.scss';
import { transformLinebreaks } from './portalUtils';
import {
    applyTranslations,
    getTranslatableFields,
    getTranslationKey,
    getTranslationState,
    initializeNewTranslationKey,
    pathToFieldName,
    setOrUnsetKeyInPayload,
    sheetTranslationsToLocalizations,
    updateLocalizationsAndPayload,
    updatePostLocalizationsArray,
} from './translationUtils';

export interface PortalTranslationManagerProps {
    postId: string;
    postPayload: PostData;
    onPostPayloadChange?: (value: PostData) => void;
}

export const PortalTranslationManager = ({
    postId,
    postPayload,
    onPostPayloadChange,
}: PortalTranslationManagerProps) => {
    const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
    const { setLocalizations } = usePortalPostEditor();
    const [pendingKeys, setPendingKeys] = useState<Record<string, string>>({});
    const {
        data: translationsRaw,
        isFetching: isFetchingTranslations,
        refetch: refetchTranslations,
    } = useTranslations('CMS', '');

    const translatableFields = useMemo(
        () => (postPayload ? getTranslatableFields(postPayload) : []),
        [postPayload],
    );

    const translations = useMemo(() => {
        return _.mapValues(translationsRaw, (translationTable) =>
            _.mapValues(translationTable, (value) =>
                transformLinebreaks(value),
            ),
        );
    }, [translationsRaw]);
    const keyDatalistId = useId();

    const { selectedLanguage } = useSelectedLanguage();
    const allLanguages = Object.values(LanguageType);
    const {
        localizations,
        localizationRecord,
        postLocalizations,
        setPostLocalizations,
    } = usePortalPostEditor();

    const allKeys = useMemo(() => {
        const sheetKeys = Object.keys(translations);
        const localizationKeys = Object.keys(localizationRecord ?? {});
        return Array.from(new Set([...sheetKeys, ...localizationKeys]));
    }, [translations, localizationRecord]);

    const translationState = useMemo(() => {
        if (!postId || !postPayload) {
            return;
        }
        return getTranslationState(
            postId,
            translatableFields,
            postPayload,
            translations ?? {},
            localizationRecord,
        );
    }, [
        postId,
        translations,
        translatableFields,
        postPayload,
        localizationRecord,
    ]);
    const onLoadTranslations = () => {
        if (!postId || !localizationRecord) {
            return;
        }
        if (!translationsRaw) return;
        const usedKeys = translatableFields
            .map((field) => {
                const translationTable = _.get(postPayload, field);
                return (
                    translationTable?.$key ?? getTranslationKey(postId, field)
                );
            })
            .filter(Boolean);
        const newLocalizations = mergeSheetTranslationsWithLocalizations(
            usedKeys,
            translationsRaw,
        );
        setLocalizations(newLocalizations);
        const newPayload = applyTranslations(
            postId,
            translatableFields,
            postPayload,
        );
        onPostPayloadChange?.(newPayload);
    };

    const copyTranslationTable = () => {
        const translationValues = translatableFields
            .map((fieldPath) => {
                const translationTable = _.get(postPayload, fieldPath);
                assert(isTranslationTable(translationTable));

                const manualKey = translationTable['$key'];

                return {
                    key: manualKey ?? getTranslationKey(postId, fieldPath),
                    path: fieldPath,
                };
            })
            .filter((value) => !(value.key in translations));

        const table = translationValues
            .map((value) => {
                const translationTable = _.get(postPayload, value.path);
                assert(
                    isRecord(translationTable),
                    "A translatable field doesn't have a translation table stored in it",
                );
                const englishValue = transformLinebreaks(
                    translationTable['en'] ?? '',
                );
                return `${value.key}\t\t${englishValue}`;
            })
            .join('\n');

        navigator.clipboard.writeText(table);
    };

    function handleModalClose() {
        // We only want to do all of this if we changed keys
        if (Object.keys(pendingKeys).length === 0) {
            setIsKeyModalOpen(false);
            return;
        }
        const newPayload = _.cloneDeep(postPayload);
        let updatedPostLocalizations = [...postLocalizations];
        let updatedLocalizations = [...localizations];

        translatableFields.forEach((field) => {
            const path = field.join('.');
            if (pendingKeys[path] === undefined) return;

            const translationTable = _.get(postPayload, field);
            const currentKey =
                translationTable?.$key ?? getTranslationKey(postId, field);
            const newKey = pendingKeys[path];
            if (newKey === currentKey) return;

            setOrUnsetKeyInPayload(newPayload, field, newKey);

            const keyExistsInSheet = newKey && translationsRaw?.[newKey];
            const keyExistsInLoc =
                newKey &&
                (localizations.some((loc) => loc.id === newKey) ||
                    Boolean(localizationRecord?.[newKey]));

            if (newKey && !keyExistsInSheet && !keyExistsInLoc) {
                const result = initializeNewTranslationKey(
                    newPayload,
                    field,
                    newKey,
                    allLanguages,
                    updatedLocalizations,
                    updatedPostLocalizations,
                    path,
                    postId,
                );
                updatedLocalizations = result.updatedLocalizations;
                updatedPostLocalizations = result.updatedPostLocalizations;
            } else {
                updatedPostLocalizations = updatePostLocalizationsArray(
                    path,
                    newKey,
                    updatedPostLocalizations,
                    postId,
                );
                updateLocalizationsAndPayload(
                    field,
                    newKey,
                    updatedLocalizations,
                    updatedLocalizations,
                    newPayload,
                    translationsRaw ?? {},
                );
            }
        });

        setPostLocalizations(updatedPostLocalizations);
        setLocalizations(updatedLocalizations);
        onPostPayloadChange?.(newPayload);
        setIsKeyModalOpen(false);
        setPendingKeys({});
    }

    function mergeSheetTranslationsWithLocalizations(
        usedKeys: string[],
        translationsRaw: Record<string, Record<string, string>>,
    ): PortalLocalizationDTO[] {
        // The flatten lives in `translationUtils` so posts and categories
        // share it. Posts additionally carry the post-specific fields below.
        return sheetTranslationsToLocalizations(usedKeys, translationsRaw).map(
            (row) => ({
                ...row,
                postLocalizations: [],
                metadataId: null,
                metadata: undefined,
            }),
        );
    }

    return (
        <fieldset className={styles.translationsContainer}>
            <legend className={styles.translationsHeaderContainer}>
                Translations
                <Button
                    onClick={copyTranslationTable}
                    color="secondary"
                    size="small"
                >
                    Copy Keys
                </Button>
                <Button
                    onClick={() => setIsKeyModalOpen(true)}
                    color="secondary"
                    size="small"
                >
                    Manage Keys
                </Button>
                {isKeyModalOpen && (
                    <ModalV2 onClose={handleModalClose}>
                        <table className={styles.manageKeysTable}>
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Translation Key</th>
                                    <th>
                                        Translation from Google Sheet (
                                        {selectedLanguage})
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {translatableFields.map((field) => {
                                    const translationTable = _.get(
                                        postPayload,
                                        field,
                                    );
                                    assert(
                                        isTranslationTable(translationTable),
                                    );
                                    const activeTranslationKey =
                                        translationTable['$key'] ??
                                        getTranslationKey(postId, field);
                                    return (
                                        <tr
                                            key={getTranslationKey(
                                                postId,
                                                field,
                                            )}
                                        >
                                            <td>
                                                <p
                                                    className={
                                                        styles.fieldDisplayName
                                                    }
                                                >
                                                    {pathToFieldName(field)}
                                                </p>
                                            </td>
                                            <td>
                                                <Input
                                                    label={getTranslationKey(
                                                        postId,
                                                        field,
                                                    )}
                                                    inputSize={50}
                                                    value={
                                                        pendingKeys[
                                                            field.join('.')
                                                        ] ??
                                                        translationTable[
                                                            '$key'
                                                        ] ??
                                                        ''
                                                    }
                                                    list={keyDatalistId}
                                                    onChange={(e) =>
                                                        setPendingKeys(
                                                            (prev) => ({
                                                                ...prev,
                                                                [field.join(
                                                                    '.',
                                                                )]:
                                                                    e.target
                                                                        .value,
                                                            }),
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <p>
                                                    {
                                                        translations[
                                                            activeTranslationKey
                                                        ]?.[selectedLanguage]
                                                    }
                                                </p>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <datalist id={keyDatalistId}>
                            {allKeys.map((key) => (
                                <option value={key} key={key} />
                            ))}
                        </datalist>
                        <ModalV2Footer>
                            <Button onClick={handleModalClose}>Close</Button>
                        </ModalV2Footer>
                    </ModalV2>
                )}
            </legend>
            {translationState?.hasNewTranslations ? (
                <>
                    <p>⚠️ There are unsynced translations</p>
                    <Button onClick={onLoadTranslations} color="primary">
                        Load from Google Sheets
                    </Button>
                    <div>
                        {translationState &&
                        translationState.missingTranslations.length > 0 ? (
                            <>
                                <p>⚠️ There are translations missing for:</p>
                                <ul>
                                    {translationState.missingTranslations.map(
                                        ({
                                            translationKey,
                                            missingLanguages,
                                        }) => (
                                            <li key={translationKey}>
                                                {translationKey}{' '}
                                                <span
                                                    title={missingLanguages.join(
                                                        ', ',
                                                    )}
                                                >
                                                    ({missingLanguages.length}{' '}
                                                    missing translations)
                                                </span>
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </>
                        ) : isFetchingTranslations ? (
                            <p>Checking for new translations...</p>
                        ) : (
                            <p>✅ Translations are synchronized</p>
                        )}
                    </div>
                    <Button
                        onClick={() => refetchTranslations()}
                        loading={isFetchingTranslations}
                        color="secondary"
                    >
                        Check again
                    </Button>
                </>
            ) : (
                <>
                    <div>
                        {translationState &&
                        translationState.missingTranslations.length > 0 ? (
                            <>
                                <p>⚠️ There are translations missing for:</p>
                                <ul>
                                    {translationState.missingTranslations.map(
                                        ({
                                            translationKey,
                                            missingLanguages,
                                        }) => (
                                            <li key={translationKey}>
                                                {translationKey}{' '}
                                                <span
                                                    title={missingLanguages.join(
                                                        ', ',
                                                    )}
                                                >
                                                    ({missingLanguages.length}{' '}
                                                    missing translations)
                                                </span>
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </>
                        ) : isFetchingTranslations ? (
                            <p>Checking for new translations...</p>
                        ) : (
                            <p>✅ Translations are synchronized</p>
                        )}
                    </div>
                    <Button
                        onClick={() => refetchTranslations()}
                        loading={isFetchingTranslations}
                        color="secondary"
                    >
                        Check again
                    </Button>
                </>
            )}
        </fieldset>
    );
};
